import { Button, Form, Input, Modal, Table, message } from "antd";
import axios from "axios";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../../nestserver";

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const SupportStaffPage = () => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const staff = useQuery("users/support-staff", async () => {
    const res = await axios.get(`${serverUrl}/users?role=support_staff`, {
      headers: authHeaders(),
    });
    return res.data;
  });

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      first_name: row?.support_staff?.first_name,
      last_name: row?.support_staff?.last_name,
      designation: row?.support_staff?.designation,
      address: row?.support_staff?.address,
      email: row?.email,
      phone: row?.phone,
      password: "",
    });
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onFinish = (values) => {
    setSaving(true);
    const request = editing
      ? axios({
          method: "patch",
          url: `${serverUrl}/support-staff/${editing._id}`,
          data: { ...values, email: values.email || "" },
          headers: authHeaders(),
        })
      : axios({
          method: "post",
          url: `${serverUrl}/users/`,
          data: { ...values, role: "support_staff" },
          headers: authHeaders(),
        });
    request
      .then((res) => {
        const wasEditing = !!editing;
        closeModal();
        queryClient.invalidateQueries("users/support-staff");
        if (wasEditing) {
          message.success("Support staff updated");
        } else {
          Modal.success({
            title: "Support staff added",
            content: `Support Staff ID: ${
              res?.data?.username || "-"
            }. They can log in with this ID and the password you set, or with OTP on their phone.`,
          });
        }
      })
      .catch((err) => {
        message.error(
          err?.response?.data?.message ||
            (editing
              ? "Could not update support staff"
              : "Could not add support staff"),
        );
      })
      .finally(() => setSaving(false));
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, row) =>
        [row?.support_staff?.first_name, row?.support_staff?.last_name]
          .filter(Boolean)
          .join(" ") || "-",
    },
    { title: "Support Staff ID", dataIndex: "username", key: "username" },
    {
      title: "Designation",
      key: "designation",
      render: (_, row) => row?.support_staff?.designation || "-",
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
        <Button type="link" onClick={() => openEdit(row)}>
          Edit
        </Button>
      ),
    },
  ];

  const rows = Array.isArray(staff.data) ? staff.data : [];

  return (
    <div className="p-8">
      <div className="bg-white p-10 rounded-2xl">
        <div className="flex items-center justify-between mb-6">
          <div className="text-2xl text-orange-500 font-semibold">
            Support Staff
          </div>
          <Button type="primary" className="h-10" onClick={openAdd}>
            Add Support Staff
          </Button>
        </div>
        <Table
          rowKey="_id"
          loading={staff.isLoading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 10 }}
        />
      </div>

      <Modal
        title={editing ? "Edit Support Staff" : "Add Support Staff"}
        open={open}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={editing ? "Save" : "Add"}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="First Name"
            name="first_name"
            rules={[{ required: true, message: "Please enter first name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Last Name"
            name="last_name"
            rules={[{ required: true, message: "Please enter last name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Designation" name="designation">
            <Input placeholder="e.g. Security, Office Assistant" />
          </Form.Item>
          <Form.Item
            label="Email (optional)"
            name="email"
            rules={[{ type: "email", message: "Enter a valid email" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Phone (used for OTP login)"
            name="phone"
            rules={[
              { required: true, message: "Please enter phone number" },
              { pattern: /^[0-9]{10}$/, message: "Enter a 10-digit number" },
            ]}
            getValueFromEvent={(e) =>
              e.target.value.replace(/\D/g, "").slice(0, 10)
            }
          >
            <Input maxLength={10} />
          </Form.Item>
          <Form.Item
            label={editing ? "New Password (leave blank to keep)" : "Password"}
            name="password"
            rules={[
              { required: !editing, message: "Please set a password" },
              { min: 6, message: "At least 6 characters" },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item label="Address" name="address">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupportStaffPage;
